import React, { useState, useEffect, useCallback, useRef } from 'react';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import Fade from '@mui/material/Fade';
import AutoStoriesIcon from '@mui/icons-material/AutoStories';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import { useDispatch } from 'react-redux';
import { useGetProfilesPinDataForDefaultPointQuery } from '../../features/profilesSlice';
import { profileApi } from '../../features/profilesSlice';
import { setPublicPinClicked } from '../../features/dataProcessingSlice';
import { activateMarker } from '../../features/activeMarkers';
import { getSearchByCoordinates } from '../../features/searchSlice';
import type { AppDispatch } from '../../app/store';
import { PinChip } from '../MessageProfile';
import { FloatingContainerMorph } from './FloatingContainerMorph';

const MIN_STORY_LENGTH = 100;
const ROTATE_INTERVAL_MS = 30000;
const MAX_FETCH_ATTEMPTS = 20;

// Module-level cache so story pool survives component remounts
let cachedStoryPool: StoryProfile[] = [];
let cachedTriedIds = new Set<number>();
let poolBuildingDone = false;
let fetchingInProgress = false;

interface StoryProfile {
  id: number;
  name_public: string;
  type: string;
  comment: string;
  formattedsearchterm?: string;
  lat: number;
  lng: number;
}

interface FloatingPatientStoriesProps {
  minimized?: boolean;
  onMinimize?: () => void;
  onExpand?: () => void;
  onStoryClick?: () => void;
  fullWidth?: boolean;
}

export const FloatingPatientStories: React.FC<FloatingPatientStoriesProps> = ({ minimized = false, onMinimize, onExpand, onStoryClick, fullWidth = false }) => {
  const dispatch = useDispatch<any>();
  const { data: pinData, isSuccess: isPinDataLoaded } = useGetProfilesPinDataForDefaultPointQuery();
  const [fetchProfile] = profileApi.endpoints.getProfile.useLazyQuery();

  const [storyPool, setStoryPool] = useState<StoryProfile[]>(cachedStoryPool);
  const [activeIndex, setActiveIndex] = useState(0);
  const [fadeIn, setFadeIn] = useState(true);
  const [isLoading, setIsLoading] = useState(cachedStoryPool.length === 0 && !poolBuildingDone);

  const rotateTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const touchStartX = useRef<number | null>(null);

  // Shuffle helper
  const pickRandomId = useCallback((pins: any[]): number | null => {
    const untried = pins.filter((p: any) => !cachedTriedIds.has(p.id));
    if (untried.length === 0) return null;
    return untried[Math.floor(Math.random() * untried.length)].id;
  }, []);

  // Build pool of profiles with decent stories
  useEffect(() => {
    if (!isPinDataLoaded || !pinData?.length || fetchingInProgress || poolBuildingDone) return;
    fetchingInProgress = true;

    const buildPool = async () => {
      let attempts = 0;
      while (attempts < MAX_FETCH_ATTEMPTS && cachedTriedIds.size < pinData.length) {
        const id = pickRandomId(pinData);
        if (id === null) break;
        cachedTriedIds.add(id);
        attempts++;

        try {
          const result = await fetchProfile(id, true).unwrap();
          if (result?.comment && result.comment.length >= MIN_STORY_LENGTH) {
            // lat/lng come from pinData (getProfile doesn't return coordinates)
            const pin = pinData.find((p: any) => p.id === id);
            const story: StoryProfile = {
              id: result.id,
              name_public: result.name_public,
              type: result.type,
              comment: result.comment,
              formattedsearchterm: result.formattedsearchterm,
              lat: pin?.lat,
              lng: pin?.lng,
            };
            // Avoid duplicates in module cache
            if (!cachedStoryPool.some(s => s.id === story.id)) {
              cachedStoryPool = [...cachedStoryPool, story];
            }
            setStoryPool([...cachedStoryPool]);
            setIsLoading(false);
          }
        } catch {
          // Skip failed fetches
        }
      }
      fetchingInProgress = false;
      poolBuildingDone = true;
      setIsLoading(false);
    };

    buildPool();
  }, [isPinDataLoaded, pinData, fetchProfile, pickRandomId]);

  // Navigate to a specific story index with fade transition
  const navigateToStory = useCallback((index: number) => {
    setFadeIn(false);
    setTimeout(() => {
      setActiveIndex(index);
      setFadeIn(true);
    }, 300);
  }, []);

  // Reset auto-rotate timer (called on manual navigation)
  const resetRotateTimer = useCallback(() => {
    if (rotateTimerRef.current) clearInterval(rotateTimerRef.current);
    if (storyPool.length <= 1) return;
    rotateTimerRef.current = setInterval(() => {
      setFadeIn(false);
      setTimeout(() => {
        setActiveIndex(prev => (prev + 1) % storyPool.length);
        setFadeIn(true);
      }, 300);
    }, ROTATE_INTERVAL_MS);
  }, [storyPool.length]);

  const goNext = useCallback(() => {
    if (storyPool.length <= 1) return;
    navigateToStory((activeIndex + 1) % storyPool.length);
    resetRotateTimer();
  }, [activeIndex, storyPool.length, navigateToStory, resetRotateTimer]);

  const goPrev = useCallback(() => {
    if (storyPool.length <= 1) return;
    navigateToStory((activeIndex - 1 + storyPool.length) % storyPool.length);
    resetRotateTimer();
  }, [activeIndex, storyPool.length, navigateToStory, resetRotateTimer]);

  // Touch swipe handlers
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  }, []);

  const handleTouchEnd = useCallback((e: React.TouchEvent) => {
    if (touchStartX.current === null) return;
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    const SWIPE_THRESHOLD = 50;
    if (delta < -SWIPE_THRESHOLD) goNext();
    else if (delta > SWIPE_THRESHOLD) goPrev();
    touchStartX.current = null;
  }, [goNext, goPrev]);

  // Rotate stories every 30 seconds
  useEffect(() => {
    if (storyPool.length <= 1) return;
    rotateTimerRef.current = setInterval(() => {
      setFadeIn(false);
      setTimeout(() => {
        setActiveIndex(prev => (prev + 1) % storyPool.length);
        setFadeIn(true);
      }, 300);
    }, ROTATE_INTERVAL_MS);
    return () => { if (rotateTimerRef.current) clearInterval(rotateTimerRef.current); };
  }, [storyPool.length]);

  const activeStory = storyPool.length > 0 ? storyPool[activeIndex % storyPool.length] : null;

  const handleStoryClick = () => {
    if (!activeStory) return;
    dispatch(getSearchByCoordinates({ lat: activeStory.lat, lng: activeStory.lng }));
    dispatch(activateMarker({ id: activeStory.id, disableHover: true }));
    dispatch(setPublicPinClicked(activeStory.id));
    onStoryClick?.();
  };

  // Don't render anything if no stories found after loading
  if (!isLoading && storyPool.length === 0) return null;

  return (
    <FloatingContainerMorph
      minimized={minimized}
      onMinimize={onMinimize}
      onExpand={onExpand}
      icon={<AutoStoriesIcon sx={{ color: '#656EFF', fontSize: 20 }} />}
      title="Patient Stories"
      sx={{ width: fullWidth ? '100%' : undefined }}
    >
      {/* Story content */}
      <Box sx={{ px: 2, pb: 2 }}>
        {pinData?.[0]?.count_total && (
          <Typography variant="body2" sx={{ color: '#777', fontSize: 13, mb: 1 }}>
            {pinData[0].count_total} patients sharing their journeys
          </Typography>
        )}
        {isLoading ? (
          <Stack gap={1}>
            <Skeleton animation="wave" variant="rounded" width="60%" height={20} />
            <Skeleton animation="wave" variant="rounded" width="100%" height={60} />
          </Stack>
        ) : activeStory ? (
          <Fade in={fadeIn} timeout={400}>
            <Stack
              gap={1.5}
              onTouchStart={handleTouchStart}
              onTouchEnd={handleTouchEnd}
            >
              {/* Name + type badge */}
              <Stack direction="row" alignItems="center" gap={1}>
                <Typography
                  onClick={handleStoryClick}
                  sx={{ fontWeight: 700, fontSize: 14, color: '#656EFF', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                >
                  {activeStory.name_public} ›
                </Typography>
                <PinChip
                  category="User"
                  type={activeStory.type}
                  size="small"
                  iconSizePx={14}
                  sx={{ height: '22px', fontSize: '0.65rem', flexShrink: 0 }}
                />
              </Stack>

              {/* Quote block */}
              <Box
                sx={{
                  position: 'relative',
                  borderLeft: '3px solid rgba(101, 110, 255, 0.4)',
                  pl: 2,
                  py: 0.5,
                  backgroundColor: 'rgba(255, 255, 255, 0.35)',
                  borderRadius: 1,
                  cursor: 'default',
                }}
              >
                <FormatQuoteIcon
                  sx={{
                    position: 'absolute',
                    top: -4,
                    left: 4,
                    fontSize: 18,
                    color: 'rgba(101, 110, 255, 0.3)',
                    transform: 'scaleX(-1)',
                  }}
                />
                <Typography
                  variant="body2"
                  data-base-ui-swipe-ignore
                  data-scroll-chain
                  sx={{
                    color: '#444',
                    lineHeight: 1.6,
                    maxHeight: 140,
                    overflowY: 'auto',
                    overscrollBehavior: 'none',
                    pr: 0.5,
                    '&::-webkit-scrollbar': { width: 4 },
                    '&::-webkit-scrollbar-thumb': {
                      backgroundColor: 'rgba(101, 110, 255, 0.3)',
                      borderRadius: 2,
                    },
                  }}
                >
                  {activeStory.comment}
                </Typography>
              </Box>

              {/* Location if available */}
              {activeStory.formattedsearchterm && (
                <Typography variant="caption" sx={{ color: '#888', fontSize: 11 }}>
                  {activeStory.formattedsearchterm}
                </Typography>
              )}

              {/* Story count indicator */}
              {storyPool.length > 1 && (
                <Stack direction="row" alignItems="center" justifyContent="center" gap={0.5} sx={{ pt: 0.5 }}>
                  {storyPool.map((_, i) => (
                    <Box
                      key={i}
                      onClick={() => {
                        if (i !== activeIndex % storyPool.length) {
                          navigateToStory(i);
                          resetRotateTimer();
                        }
                      }}
                      sx={{
                        width: i === activeIndex % storyPool.length ? 16 : 6,
                        height: 6,
                        borderRadius: 3,
                        cursor: 'pointer',
                        backgroundColor: i === activeIndex % storyPool.length
                          ? 'rgba(101, 110, 255, 0.7)'
                          : 'rgba(101, 110, 255, 0.2)',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          backgroundColor: i === activeIndex % storyPool.length
                            ? 'rgba(101, 110, 255, 0.85)'
                            : 'rgba(101, 110, 255, 0.4)',
                        },
                      }}
                    />
                  ))}
                </Stack>
              )}
            </Stack>
          </Fade>
        ) : null}
      </Box>
    </FloatingContainerMorph>
  );
};
